import axios from "axios";
import { format } from "date-fns";

// Normalize API base URL: if env lacks scheme, default to https://
const RAW_API = process.env.REACT_APP_API_URL;
const API_BASE_URL = RAW_API
  ? RAW_API.startsWith("http://") || RAW_API.startsWith("https://")
    ? RAW_API
    : `https://${RAW_API}`
  : "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export interface TimeSlot {
  id: number;
  slot_datetime: string;
  is_available: boolean;
  status?: "available" | "booked" | "blocked";
}

export interface AppointmentData {
  patient_surname: string;
  patient_name: string;
  amka: string;
  email: string;
  phone: string;
  appointment_datetime: string;
  notes?: string;
  is_returning_patient?: boolean;
}

export interface Appointment {
  id: number;
  patient_surname: string;
  patient_name: string;
  amka: string;
  email: string;
  phone?: string;
  appointment_datetime: string;
  is_confirmed: boolean;
  notes?: string;
  created_at: string;
}

// Mirrors the backend's AppointmentCancelDetails: deliberately narrower than
// Appointment, with no amka/email/phone, because the cancel token alone unlocks it.
export interface AppointmentCancelDetails {
  appointment_datetime: string;
  patient_name: string;
  duration_minutes: number;
}

export interface ChatbotMessage {
  message: string;
  session_id?: string;
}

export interface ChatbotResponse {
  response: string;
  session_id: string;
  suggested_actions?: string[];
}

export const appointmentApi = {
  getAvailableSlots: async (
    startDate?: Date,
    endDate?: Date,
  ): Promise<TimeSlot[]> => {
    const params: any = {};
    if (startDate) {
      params.start_date = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
    }
    if (endDate) {
      params.end_date = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
    }
    const response = await api.get("/api/slots/available", { params });
    return response.data;
  },

  bookAppointment: async (data: AppointmentData): Promise<Appointment> => {
    const response = await api.post("/api/appointments/book", data);
    return response.data;
  },

  confirmAppointment: async (
    token: string,
  ): Promise<{
    message: string;
    appointment_id: number;
    calendar_synced: boolean;
  }> => {
    const response = await api.post("/api/appointments/confirm", { token });
    return response.data;
  },

  // Read-only. Loading the cancellation page must never mutate anything, so the
  // page fetches the appointment through this and cancels only on a click.
  getAppointmentByCancelToken: async (
    token: string,
  ): Promise<AppointmentCancelDetails> => {
    const response = await api.get(
      `/api/appointments/by-token/${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  cancelAppointmentByToken: async (
    token: string,
  ): Promise<{ message: string }> => {
    const response = await api.post("/api/appointments/cancel", { token });
    return response.data;
  },
};

export const chatbotApi = {
  sendMessage: async (
    message: string,
    sessionId?: string,
  ): Promise<ChatbotResponse> => {
    const response = await api.post("/api/chatbot/message", {
      message,
      session_id: sessionId,
    } as ChatbotMessage);
    return response.data;
  },

  clearSession: async (sessionId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/chatbot/session/${sessionId}`);
    return response.data;
  },
};

export const adminApi = {
  listSlots: async (
    startDate: Date,
    endDate: Date,
    token: string,
  ): Promise<TimeSlot[]> => {
    const params: any = {};
    params.start_date = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
    params.end_date = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
    const response = await api.get("/api/admin/slots/list", {
      params,
      headers: { "X-Admin-Token": token },
    });
    return response.data;
  },

  createRange: async (
    startDate: Date,
    endDate: Date,
    token: string,
  ): Promise<{ message: string; added: number }> => {
    const response = await api.post(
      "/api/admin/slots/create_range",
      {
        start_date: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        end_date: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      },
      {
        headers: { "X-Admin-Token": token },
      },
    );
    return response.data;
  },

  deleteRange: async (
    startDate: Date,
    endDate: Date,
    token: string,
  ): Promise<{ message: string; deleted: number }> => {
    const response = await api.delete("/api/admin/slots/delete_range", {
      data: {
        start_date: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        end_date: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      },
      headers: { "X-Admin-Token": token },
    });
    return response.data;
  },

  blockRange: async (
    startDate: Date,
    endDate: Date,
    token: string,
  ): Promise<{ message: string; blocked: number; tombstoned: number }> => {
    const response = await api.post(
      "/api/admin/slots/block_range",
      {
        start_date: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        end_date: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      },
      {
        headers: { "X-Admin-Token": token },
      },
    );
    return response.data;
  },
};

export default api;
